import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Slider } from '../../ui/slider';

/**
 * Basic Inputs Step - Core retirement planning parameters
 */
export default function BasicInputsStep({ inputs, updateInput, updateSpendingSplit, country, currencySymbol }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Retirement Parameters</CardTitle>
        <CardDescription>
          Configure your core retirement assumptions. This is the foundation of your retirement plan.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Age Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="currentAge">Current Age</Label>
            <Input
              id="currentAge"
              type="number"
              value={inputs.currentAge}
              onChange={(e) => updateInput('currentAge', parseInt(e.target.value))}
              min="18"
              max="100"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="retirementAge">Desired Retirement Age</Label>
            <Input
              id="retirementAge"
              type="number"
              value={inputs.retirementAge}
              onChange={(e) => updateInput('retirementAge', parseInt(e.target.value))}
              min={inputs.currentAge + 1}
              max="100"
            />
            <p className="text-xs text-muted-foreground">
              When you want to retire
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="statePensionAge">
              {country === 'UK' ? 'State Pension Age' : 'Social Security Age'}
            </Label>
            <Input
              id="statePensionAge"
              type="number"
              value={inputs.statePensionAge}
              onChange={(e) => updateInput('statePensionAge', parseInt(e.target.value))}
              min="62"
              max="70"
            />
            <p className="text-xs text-muted-foreground">
              {country === 'UK'
                ? 'When State Pension starts (usually 66-67)'
                : 'When Social Security starts (62-70)'}
            </p>
          </div>
        </div>

        {/* Life Expectancy */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="lifeExpectancy">Life Expectancy</Label>
            <Input
              id="lifeExpectancy"
              type="number"
              value={inputs.lifeExpectancy}
              onChange={(e) => updateInput('lifeExpectancy', parseInt(e.target.value))}
              min={inputs.retirementAge + 1}
              max="120"
            />
            <p className="text-xs text-muted-foreground">
              Plan for longevity. UK average: 81 years, US average: 79 years
            </p>
          </div>
        </div>

        {/* Income Goals */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="desiredAnnualIncome">Desired Annual Retirement Income</Label>
            <Input
              id="desiredAnnualIncome"
              type="number"
              value={inputs.desiredAnnualIncome}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                updateInput('desiredAnnualIncome', value);
                // Auto-adjust essential/discretionary split
                updateInput('essentialSpending', value * 0.7);
                updateInput('discretionarySpending', value * 0.3);
              }}
              min="0"
              step="1000"
            />
            <p className="text-xs text-muted-foreground">
              How much do you want to spend per year in retirement?
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="currentAnnualIncome">Current Annual Income</Label>
            <Input
              id="currentAnnualIncome"
              type="number"
              value={inputs.currentAnnualIncome}
              onChange={(e) => updateInput('currentAnnualIncome', parseInt(e.target.value))}
              min="0"
              step="1000"
            />
            <p className="text-xs text-muted-foreground">
              Used to calculate income replacement ratio
            </p>
          </div>
        </div>

        {/* Essential vs Discretionary Spending */}
        <div className="space-y-2">
          <Label>Essential vs Discretionary Spending</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Essential spending (housing, food, healthcare) vs discretionary (travel, hobbies)
          </p>
          <div className="flex items-center gap-4">
            <span className="text-sm w-24">Essential:</span>
            <Slider
              value={[(inputs.essentialSpending / inputs.desiredAnnualIncome) * 100]}
              onValueChange={([value]) => updateSpendingSplit(value)}
              max={100}
              step={5}
              className="flex-1"
            />
            <span className="text-sm w-32 text-right">
              {currencySymbol}{inputs.essentialSpending.toLocaleString()} ({Math.round((inputs.essentialSpending / inputs.desiredAnnualIncome) * 100)}%)
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm w-24">Discretionary:</span>
            <div className="flex-1 bg-muted h-2 rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${(inputs.discretionarySpending / inputs.desiredAnnualIncome) * 100}%` }}
              />
            </div>
            <span className="text-sm w-32 text-right">
              {currencySymbol}{inputs.discretionarySpending.toLocaleString()} ({Math.round((inputs.discretionarySpending / inputs.desiredAnnualIncome) * 100)}%)
            </span>
          </div>
        </div>

        {/* Withdrawal Strategy */}
        <div className="space-y-2">
          <Label htmlFor="withdrawalStrategy">Withdrawal Strategy</Label>
          <Select value={inputs.withdrawalStrategy} onValueChange={(value) => updateInput('withdrawalStrategy', value)}>
            <SelectTrigger id="withdrawalStrategy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">Fixed Amount</SelectItem>
              <SelectItem value="percentage">Fixed Percentage (4% Rule)</SelectItem>
              <SelectItem value="inflationAdjusted">Inflation-Adjusted (Recommended)</SelectItem>
              <SelectItem value="guardrails">Dynamic Guardrails</SelectItem>
              <SelectItem value="dynamic">Dynamic (Portfolio-Based)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How you'll withdraw money from your portfolio during retirement
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
